<template>
  <div class="mc-video-history-new__filelist-table-wrapper">
    <b-table
      :items="calculatedLazyFilelist"
      :fields="fieldsTable"
      class="border border-1 mc-video-history-new__filelist-table"
      hover
      responsive
      small
      show-empty
      :busy="loadingFilelist"
      :empty-text="$t('global.emptyTable')"
    >
      <template #table-busy>
        <div class="text-center my-3">
          <b-spinner />
        </div>
      </template>

      <template #head()="data">
        <div
          class="mc-video-history-new__filelist-table-head"
          @click="handleClickHeader(data.field.key, data.field.isSortable)"
        >
          <span>
            {{ data.label }}
          </span>

          <div v-if="data.field.isSortable" class="icon-sort-wrapper">
            <template v-if="sortKey === data.field.key">
              <i v-if="sortDirection === 'asc'" class="mc-icon icon-outline_chevron-up"></i>
              <i v-else class="mc-icon icon-outline_chevron-down"></i>
            </template>

            <template v-else>
              <i class="mc-icon icon-outline_chevron-up"></i>
              <i class="mc-icon icon-outline_chevron-down"></i>
            </template>
          </div>
        </div>
      </template>
      <template #cell(togglePlay)="data">
        <span
          class="toggle-play-wrapper"
          @click="
            handleTogglePlay(
              data.item.mediaFilename,
              data.item.channel,
              data.item.startTime,
              data.item.endTime
            )
          "
        >
          <i
            v-if="isFilelistPlay(data.item.mediaFilename, data.item.channel)"
            class="icon-fill_pause"
          />
          <i v-else class="icon-fill_play" />
        </span>
      </template>
      <template #cell(channel)="data">
        {{ getNormalizedChannel(data.item.channel) }}
      </template>
      <template #cell(action)="data">
        <McButton
          size="sm"
          icon="icon-outline_download"
          variant="outline-primary"
          :text="$t('videoHistory.modalVideoDownload.download')"
          @click="
            openModalDownload(
              data.item.mediaFilename,
              data.item.startTime,
              data.item.endTime,
              data.item.channel
            )
          "
        />
      </template>
      <template #cell(period)="data">
        {{ momentHms(data.item.startTime) + ' ' + formatLabelTimezone(deviceTimezone) }} -
        {{ momentHms(data.item.endTime) + ' ' + formatLabelTimezone(deviceTimezone) }}
      </template>
      <template #cell(alert)="data">
        <div class="mc-video-history-new__table-alert-wrapper">
          <span
            v-for="alert in data.item.alerts"
            :key="alert.rawAlertId"
            class="alert-icon-wrapper"
            :class="{ urgent: alert.isUrgent, important: alert.isImportant }"
            @mouseenter="handleAlertMouseEnter($event, alert)"
            @mouseleave="handleAlertMouseLeave"
          >
            <i :class="getAlertIcon(alert.alert.alertCode)" />
          </span>
        </div>
      </template>
      <template #cell(duration)="data">
        {{ calculatedDuration(data.item.startTime, data.item.endTime) }}
      </template>
    </b-table>

    <div
      v-if="alertTooltip.visible"
      class="mc-video-history-new__alert-tooltip"
      :style="{
        left: `${alertTooltip.left}px`,
        top: `${alertTooltip.top}px`,
      }"
    >
      <div class="mc-video-history-new__alert-tooltip-line">
        {{ alertTooltip.description }}
      </div>
    </div>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'
import { mapState, mapGetters } from 'vuex'
import { getAlertIcon } from '@/helpers/alert-icon'
import { createAlertTimeIndex, getAlertsBetween } from '../../../helper/filelist-alert-index'
import { getNextFilelistBatch } from '../../../helper/filelist-lazy-load'

export default {
  name: 'TableFilelist',
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    mediaFilenameSelected: {
      type: String,
      default: null,
    },
    channelPlay: {
      type: Number,
      default: null,
    },
  },
  emits: ['handlePlay', 'handlePause'],
  data() {
    return {
      sortKey: 'channel', // channel | period
      sortDirection: 'asc', // asc | desc
      calculatedLazyFilelist: [],
      lazyBatchSize: 20,
      lazyLoadThresholdPx: 100,
      isLoadingMore: false,
      scrollContainer: null,
      alertTooltip: {
        visible: false,
        left: 0,
        top: 0,
        description: '',
      },
    }
  },
  computed: {
    ...mapState('videoHistory', [
      'mapFilelist',
      'loadingFilelist',
      'rawAlerts',
      'mapWebviewStatePerChannel',
      'filelistPlaySelected',
      'activeTab',
    ]),
    ...mapGetters('videoHistory', ['deviceTimezone', 'statusTextActive', 'deviceTypeActive']),
    fieldsTable() {
      return [
        { key: 'togglePlay', label: '', isSortable: false },
        { key: 'channel', label: this.$t('videoHistory.channelCamera'), isSortable: true },
        { key: 'action', label: this.$t('liveView.table.fleetHistory.action'), isSortable: false },
        { key: 'period', label: this.$t('liveView.tab.period'), isSortable: true },
        { key: 'alert', label: this.$t('global.object.alert'), isSortable: false },
        { key: 'duration', label: this.$t('liveView.tempTracking.duration'), isSortable: false },
      ]
    },
    isFilelistPlay() {
      return (mediaFilename, channel) => {
        if (mediaFilename !== this.mediaFilenameSelected) return false
        const channelState = this.mapWebviewStatePerChannel[channel]
        return (
          channelState === 'CONNECTING' ||
          channelState === 'PLAYING' ||
          channelState === 'FREEZE' ||
          channelState === 'LOAD'
        )
      }
    },
    alertTimeIndex() {
      return createAlertTimeIndex(this.rawAlerts)
    },
    getNormalizedChannel() {
      return (ch) => {
        return (
          this.statusTextActive.split(',').findIndex((c) => {
            if (this.deviceTypeActive === 'jc') return Number(c) + 1 === ch
            return Number(c) === ch
          }) + 1
        )
      }
    },
    sortedFilelist() {
      const result = Object.values(this.mapFilelist).reduce((files, filelist) => {
        if (Array.isArray(filelist)) {
          files.push(...filelist)
        }

        return files
      }, [])

      const direction = this.sortDirection === 'asc' ? 1 : -1

      return result.sort((a, b) => {
        if (this.sortKey === 'channel') {
          return (Number(a.channel) - Number(b.channel)) * direction
        }

        if (this.sortKey === 'period') {
          return (new Date(a.startTime).getTime() - new Date(b.startTime).getTime()) * direction
        }

        return 0
      })
    },
    sortingSignature() {
      return `${this.sortKey}:${this.sortDirection}`
    },
    calculatedDuration() {
      return (startTime, endTime) => {
        if (!startTime || !endTime) return '-'

        const durationInSeconds = Math.max(
          0,
          Math.floor((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000)
        )

        const hours = Math.floor(durationInSeconds / 3600)
        const minutes = Math.floor((durationInSeconds % 3600) / 60)
        const seconds = durationInSeconds % 60

        const result = []

        if (hours > 0) result.push(`${hours} ${this.$t('global.time.hours')}`)
        if (minutes > 0) result.push(`${minutes} ${this.$t('global.time.minutes')}`)
        if (seconds > 0 || result.length === 0)
          result.push(`${seconds} ${this.$t('global.time.seconds')}`)

        return result.join(' ')
      }
    },
  },
  watch: {
    loadingFilelist(isLoading) {
      if (isLoading) {
        this.calculatedLazyFilelist = []
        return
      }

      if (this.activeTab === 'filelist') this.resetLazyFilelist()
    },
    activeTab(activeTab) {
      if (
        activeTab === 'filelist' &&
        !this.loadingFilelist &&
        this.calculatedLazyFilelist.length === 0
      ) {
        this.resetLazyFilelist()
      }
    },
    rawAlerts() {
      if (this.activeTab === 'filelist' && !this.loadingFilelist) {
        this.resetLazyFilelist()
      }
    },
    mapFilelist: {
      deep: true,
      handler() {
        if (this.activeTab === 'filelist' && !this.loadingFilelist) {
          this.resetLazyFilelist()
        }
      },
    },
    sortingSignature() {
      if (this.activeTab === 'filelist' && !this.loadingFilelist) {
        this.resetLazyFilelist()
      }
    },
    deviceTimezone() {
      if (this.activeTab === 'filelist' && !this.loadingFilelist) {
        this.resetLazyFilelist()
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollContainer = this.$el.closest('.mc-video-history-new__playlist-scroll-wrapper')
      this.scrollContainer?.addEventListener('scroll', this.handleLazyScroll, { passive: true })

      if (this.activeTab === 'filelist' && !this.loadingFilelist) {
        this.resetLazyFilelist()
      }
    })
  },
  beforeDestroy() {
    this.scrollContainer?.removeEventListener('scroll', this.handleLazyScroll)
  },
  methods: {
    handleAlertMouseEnter(event, alert) {
      const rect = event.currentTarget.getBoundingClientRect()
      const descriptionKey = this.$i18n.locale === 'id' ? 'descriptionIdn' : 'descriptionEng'

      this.alertTooltip.left = rect.left + rect.width / 2
      this.alertTooltip.top = rect.top - 8
      this.alertTooltip.description = alert.alert?.[descriptionKey] ?? ''
      this.alertTooltip.visible = true
    },
    handleAlertMouseLeave() {
      this.alertTooltip.visible = false
      this.alertTooltip.description = ''
    },
    enrichFileWithAlerts(file) {
      const utcStartTime = this.getUtcDateFromLocalDate(file.startTime, this.deviceTimezone)
      const utcEndTime = this.getUtcDateFromLocalDate(file.endTime, this.deviceTimezone)

      return {
        ...file,
        alerts: getAlertsBetween(this.alertTimeIndex, utcStartTime, utcEndTime),
      }
    },
    async loadMoreFilelist() {
      if (this.isLoadingMore || this.activeTab !== 'filelist') return

      const nextBatch = getNextFilelistBatch(
        this.sortedFilelist,
        this.calculatedLazyFilelist.length,
        this.lazyBatchSize
      )

      if (!nextBatch.length) return

      this.isLoadingMore = true
      const enrichedBatch = nextBatch.map(this.enrichFileWithAlerts)
      this.calculatedLazyFilelist = [...this.calculatedLazyFilelist, ...enrichedBatch]

      await this.$nextTick()
      this.isLoadingMore = false

      if (
        this.scrollContainer &&
        this.scrollContainer.scrollHeight <=
          this.scrollContainer.clientHeight + this.lazyLoadThresholdPx
      ) {
        await this.loadMoreFilelist()
      }
    },
    resetLazyFilelist() {
      this.calculatedLazyFilelist = []

      if (this.scrollContainer) this.scrollContainer.scrollTop = 0

      this.$nextTick(this.loadMoreFilelist)
    },
    handleLazyScroll() {
      if (!this.scrollContainer || this.activeTab !== 'filelist') return

      const remainingScroll =
        this.scrollContainer.scrollHeight -
        this.scrollContainer.scrollTop -
        this.scrollContainer.clientHeight

      if (remainingScroll <= this.lazyLoadThresholdPx) {
        this.loadMoreFilelist()
      }
    },
    openModalDownload(mediaFilename, startTime, endTime, channel) {
      this.$bvModal.show('modal-download-video-history')
      this.$store.commit('videoHistory/setFilelistDownloadSelected', {
        startTime: this.getUtcDateFromLocalDate(startTime, this.deviceTimezone),
        endTime: this.getUtcDateFromLocalDate(endTime, this.deviceTimezone),
        channel: channel,
      })
    },
    getAlertIcon(alertCode) {
      return `icon-${getAlertIcon(alertCode)}`
    },
    handleClickHeader(key, isSortable) {
      if (!isSortable) return
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortKey = key
      this.sortDirection = 'asc'
    },
    handleTogglePlay(mediaFilename, channelPlay, localStartTime, localEndTime) {
      const state = this.mapWebviewStatePerChannel[channelPlay]
      const isPlaying =
        state === 'CONNECTING' || state === 'PLAYING' || state === 'FREEZE' || state === 'LOAD'

      if (isPlaying && mediaFilename === this.mediaFilenameSelected) {
        this.$emit('handlePause', mediaFilename, channelPlay)
      } else {
        this.$emit(
          'handlePlay',
          mediaFilename,
          channelPlay,
          this.getUtcDateFromLocalDate(localStartTime, this.deviceTimezone),
          this.getUtcDateFromLocalDate(localEndTime, this.deviceTimezone)
        )
      }
    },
  },
}
</script>
